import {
  type ComponentProps,
  type CSSProperties,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { X } from 'lucide-react'

/** Status tones carry meaning; the category tones after them only tell groups apart. */
const BADGE_TONES = [
  'neutral',
  'brand',
  'info',
  'success',
  'warning',
  'danger',
  'yellow',
  'teal',
  'indigo',
  'purple',
  'pink',
  'brown',
] as const

type BadgeTone = (typeof BADGE_TONES)[number]

/**
 * A tone's colours as custom properties, so each variant is one class string instead of one per
 * tone. Every tone but neutral reads them off its token family.
 */
function toneStyle(tone: BadgeTone): CSSProperties {
  const vars =
    tone === 'neutral'
      ? {
          '--badge-wash': 'var(--element)',
          '--badge-wash-hover': 'var(--element-hover)',
          '--badge-wash-active': 'var(--element-active)',
          '--badge-ink': 'var(--foreground)',
          '--badge-mark': 'var(--foreground-secondary)',
          '--badge-fill': 'var(--gray-12)',
          '--badge-fill-hover': 'var(--gray-11)',
          '--badge-fill-active': 'var(--gray-11)',
          '--badge-on-fill': 'var(--gray-1)',
        }
      : {
          '--badge-wash': `var(--${tone}-subtle)`,
          '--badge-wash-hover': `var(--${tone}-subtle-hover)`,
          '--badge-wash-active': `var(--${tone}-subtle-active)`,
          '--badge-ink': `var(--${tone}-text)`,
          '--badge-mark': `var(--${tone}-text-subtle)`,
          '--badge-fill': `var(--${tone})`,
          '--badge-fill-hover': `var(--${tone}-hover)`,
          '--badge-fill-active': `var(--${tone}-active)`,
          '--badge-on-fill': `var(--text-on-${tone})`,
        }
  // React's style type has no custom properties; every value here is a string.
  return vars as CSSProperties
}

/**
 * How loud the badge is. `subtle` is a wash with no edge, the resting look of a label that does
 * nothing: an edge and a control surface are what make something read as a button, so a static
 * badge never takes them. `outline` is that control look, for a badge that is pressed (a filter, a
 * picker trigger). `solid` is a filled count or status that has to be seen from across the screen.
 *
 * `--badge-dot` colours the dot and the clear X: the tone's mark, or the label colour on a solid
 * fill, where the mark would sink.
 */
const VARIANTS = {
  subtle: [
    'bg-[var(--badge-wash)] text-[var(--badge-ink)] [--badge-dot:var(--badge-mark)]',
    'data-[interactive]:hover:bg-[var(--badge-wash-hover)] data-[interactive]:active:bg-[var(--badge-wash-active)]',
  ].join(' '),
  outline: [
    'bg-transparent text-[var(--badge-ink)] [--badge-dot:var(--badge-mark)]',
    'shadow-[inset_0_0_0_var(--border-hairline)_var(--border-strong)]',
    'data-[interactive]:hover:bg-[var(--badge-wash)] data-[interactive]:active:bg-[var(--badge-wash-hover)]',
  ].join(' '),
  solid: [
    'bg-[var(--badge-fill)] text-[var(--badge-on-fill)] [--badge-dot:currentColor]',
    'data-[interactive]:hover:bg-[var(--badge-fill-hover)] data-[interactive]:active:bg-[var(--badge-fill-active)]',
  ].join(' '),
}

const SIZES = {
  xs: '[--badge-h:16px] [--badge-pad:4px] gap-0.5 [&_img:not([class*="size-"])]:size-3 [&_svg:not([class*="size-"])]:size-3',
  sm: '[--badge-h:20px] [--badge-pad:6px] gap-1 [&_img:not([class*="size-"])]:size-3 [&_svg:not([class*="size-"])]:size-3',
  md: '[--badge-h:24px] [--badge-pad:8px] gap-1 [&_img:not([class*="size-"])]:size-3 [&_svg:not([class*="size-"])]:size-3',
  lg: '[--badge-h:28px] [--badge-pad:10px] gap-1.5 [&_img:not([class*="size-"])]:size-3.5 [&_svg:not([class*="size-"])]:size-3.5',
  xl: '[--badge-h:32px] [--badge-pad:12px] gap-1.5 text-sm [&_img:not([class*="size-"])]:size-3.5 [&_svg:not([class*="size-"])]:size-3.5',
}

const SHAPES = {
  pill: 'corner-sharp rounded-full',
  squircle: 'corner-squircle rounded-[calc(var(--badge-h)/2)]',
}

const badgeVariants = cva(
  [
    'inline-flex h-[var(--badge-h)] min-w-0 max-w-full shrink-0 items-center justify-center whitespace-nowrap px-[var(--badge-pad)] align-middle text-xs font-medium tabular-nums',
    '[&_img:not([class*="rounded-"])]:rounded-full [&_img]:shrink-0 [&_svg]:shrink-0',
    // Optical padding: a leading glyph or image pulls its edge in.
    'has-[>img:first-child]:pl-[calc(var(--badge-pad)-2px)] has-[>svg:first-child]:pl-[calc(var(--badge-pad)-2px)]',
    'data-[dot]:before:size-1.5 data-[dot]:before:shrink-0 data-[dot]:before:rounded-full data-[dot]:before:bg-[var(--badge-dot)] data-[dot]:before:content-[""]',
    'data-[interactive]:cursor-pointer data-[interactive]:select-none data-[interactive]:no-underline',
    'data-[interactive]:transition-[background-color,box-shadow] data-[interactive]:duration-150 data-[interactive]:ease-out motion-reduce:transition-none',
    'focus-visible:focus-ring data-[interactive]:hover:shadow-[inset_0_0_0_var(--border-hairline)_var(--border-hover)]',
    'aria-disabled:text-foreground-disabled disabled:text-foreground-disabled aria-disabled:cursor-not-allowed disabled:cursor-not-allowed',
  ],
  {
    variants: { variant: VARIANTS, size: SIZES, shape: SHAPES },
    defaultVariants: { variant: 'subtle', size: 'md', shape: 'pill' },
  },
)

interface BadgeBaseProps extends Omit<ComponentProps<'span'>, 'children' | 'ref'>, VariantProps<typeof badgeVariants> {
  tone?: BadgeTone
  left?: ReactNode
  right?: ReactNode
  children?: ReactNode
  /** A status dot before the label, in the tone's mark colour. */
  dot?: boolean
}

/** A static badge, optionally with a trailing remove button. */
interface StaticBadgeProps extends BadgeBaseProps {
  render?: undefined
  onClear?: MouseEventHandler<HTMLButtonElement>
  /** The remove button's accessible name. */
  clearLabel?: string
}

/**
 * A badge that is itself the control, Base UI style: `render={<button type="button" onClick={…} />}`
 * or `render={<a href="…" />}`. It takes hover, press and the focus ring. No `onClear`: buttons can't nest.
 */
interface InteractiveBadgeProps extends BadgeBaseProps {
  render: ReactElement
  onClear?: never
  clearLabel?: never
}

type BadgeProps = StaticBadgeProps | InteractiveBadgeProps

function Badge({
  left,
  right,
  dot,
  onClear,
  clearLabel = 'Remove',
  variant,
  tone = 'neutral',
  size,
  shape,
  render,
  className,
  style,
  children,
  ...props
}: BadgeProps) {
  return useRender({
    render,
    defaultTagName: 'span',
    props: {
      'data-slot': 'badge',
      'data-interactive': render ? '' : undefined,
      'data-dot': dot ? '' : undefined,
      style: { ...toneStyle(tone), ...style },
      className: twMerge(
        badgeVariants({ variant, size, shape }),
        onClear && 'pr-[calc(var(--badge-pad)-4px)]',
        className,
      ),
      ...props,
      children: (
        <>
          {left}
          {children}
          {right}
          {onClear && <BadgeClear label={clearLabel} onClick={onClear} />}
        </>
      ),
    },
  })
}

function BadgeClear({ label, onClick }: { label: string; onClick: MouseEventHandler<HTMLButtonElement> }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={twMerge(
        'relative -my-1 grid size-[max(12px,calc(var(--badge-h)-6px))] shrink-0 cursor-pointer place-items-center rounded-full',
        // The tone's mark at rest, so the X is legible on the wash before it's hovered.
        'text-[var(--badge-dot)] [&_svg]:stroke-[length:var(--icon-stroke-width-bold)]',
        'transition-[background-color,color] duration-150 ease-out motion-reduce:transition-none',
        'hover:bg-element-hover focus-visible:focus-ring active:bg-element-active hover:text-current',
        // Expand the hit target past the visual circle.
        'after:absolute after:-inset-1 after:content-[""]',
      )}
    >
      <X aria-hidden />
    </button>
  )
}

export { Badge, BADGE_TONES, type BadgeProps, type BadgeTone }
